import type { TaskId } from "@tento-lona/sheets";
import type { Option } from "@tento-lona/sheets";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";
import type { TypedApi } from "@tento-core/typed-api";
import type { CellValue, TaskCellValue } from "@tento-lona/sheets";
import { CellId, Patch, RowKey, RowNetworkKey } from "@tento-lona/sheets";
import type { Rational } from "@tento-core/rational";
import { SdkRequestError, TaskWriteError } from "../../errors.ts";
import { Network } from "../../network.ts";
import { rowHandleOf, type SheetRow } from "../../rows/sheet-row.ts";
import { Actor, type InvalidationSink, TasksChanged } from "../../invalidation.ts";
import type { RowsClient } from "../../rows/rows-client.ts";
import { requireNonEmptyKey } from "../../sync/_contracts.ts";
import { Uuid } from "@tento-core/uuid";
import { Ledger } from "@tento-lona/sheets/internal-sdk";
import type { ClientLifecycle } from "../../client/handle-errors.ts";
import { afterFailedWrite } from "../../client/after-failed-write.ts";
import { deepFrozen } from "@tento-lona/sheets/internal-sdk";

/**
 * The `TasksClient.Snapshot` decoder for a task cell's value: an object whose
 * `id` is a UUID and whose present task fields have their wire types (strings, a
 * number, hydrated `DateTime`s, a `{ n, d }` position). A cell that fails is
 * not a task and is skipped. `status` / `importance` are checked as strings
 * only: a cell decoded by the `task.v1` encoding carries the canonical
 * `dtype.TaskCellValue` status spelling, not `Network.TaskStatus`.
 */
function isTaskSnapshot(value: object): value is TasksClient.Snapshot {
  if (!("id" in value) || typeof value.id !== "string" || !Uuid.is(value.id)) {
    return false;
  }
  for (const key of ["title", "description", "displayId", "status", "importance", "eventId"]) {
    if (!optionalOf(value, key, (v) => typeof v === "string")) return false;
  }
  if (!optionalOf(value, "displayNumber", (v) => typeof v === "number")) return false;
  for (const key of ["completedAt", "createdAt", "dueDate", "scheduledStart", "scheduledEnd"]) {
    if (!optionalOf(value, key, (v) => v instanceof DateTime)) return false;
  }
  return optionalOf(value, "position", (v) =>
    typeof v === "object" && v !== null && "n" in v && "d" in v &&
    typeof v.n === "number" && typeof v.d === "number");
}

/** `true` when `value[key]` is absent, `null`, or passes `check`. */
function optionalOf(
  value: object,
  key: string,
  check: (v: NonNullable<CellFieldValue>) => boolean,
): boolean {
  const field: CellFieldValue = Reflect.get(value, key);
  return field == null || check(field);
}

/** A task cell field as read off the decoded object, before it is checked. */
type CellFieldValue = Option<string | number | boolean | object>;

const TASK_ROW_KEY = ":~lona:tasks";
const TASKS_ACTOR = new Actor("tasks-client");

/** Wide range for fetching all task cells. */
const TASK_FETCH_RANGE = new DateTime.Range(
  DateTime.fromDse(NaiveDate.fromYmd1(2020, 1, 1).exp().dse, Utc),
  DateTime.fromDse(NaiveDate.fromYmd1(2030, 1, 1).exp().dse, Utc),
);

/** Types that go with `TasksClient`: `Item`, `NewTask`, `Snapshot`, `Patch`, `Api`. */
export namespace TasksClient {
  /**
   * A held task, as a mutation reads it: its id, and the dates that locate
   * its cell (and seed the local mirror) when the task row is not loaded —
   * each `null` when unset. A wire task (`Network.Task`, the `Snapshot` from
   * `list()` / `get()` / `peek()`) is one as it is; the rows-side
   * `TaskCellValue` a calendar renderer holds (`TaskCalendarItem.task`)
   * becomes one through `Item.ofCell(cell)`. A caller passes what it holds,
   * never an id to re-resolve.
   */
  export type Item = {
    readonly id: TaskId;
    readonly scheduledStart: Option<DateTime<Utc>>;
    readonly dueDate: Option<DateTime<Utc>>;
    readonly createdAt: Option<DateTime<Utc>>;
    readonly completedAt: Option<DateTime<Utc>>;
  };

  /**
   * A new task, as `tasks.create(task, actor)` takes it: the fields a
   * caller chooses — its `title` (required: the server's title is not
   * nullable) and every other stated, `null` for one the task does not have.
   * The server-owned fields (`displayId`, `displayNumber`, `createdAt`,
   * `completedAt`) are not in it: `POST /api/tasks` assigns them, and
   * `completedAt` follows the task's `status`. The SDK encodes the request
   * body (`NewTask.encode`).
   *
   * ```ts
   * await client.tasks.create({
   *   title: "Review SDK examples", status: "inProgress", importance: "high",
   *   description: null, dueDate: null, eventId: null, position: null,
   *   scheduledStart: null, scheduledEnd: null,
   * }, Actor.EXTERNAL);
   * ```
   */
  export type NewTask = {
    readonly title: string;
    readonly description: Option<string>;
    readonly dueDate: Option<DateTime<Utc>>;
    readonly eventId: Option<NonNullable<Network.TaskAttributes["eventId"]>>;
    readonly importance: Option<Network.TaskPriority>;
    readonly position: Option<Rational>;
    readonly scheduledStart: Option<DateTime<Utc>>;
    readonly scheduledEnd: Option<DateTime<Utc>>;
    readonly status: Option<Network.TaskStatus>;
  };

  /**
   * A task as the client returns it: its wire attributes
   * (`Network.TaskAttributes`) plus a guaranteed `id`. `list()` / `get()` /
   * `peek()` / `create()` return it, the local mirror writes it into the
   * task's cell, and `merge` reads and returns it. A `Snapshot` is an `Item`
   * (pass it straight back to a mutation).
   */
  export type Snapshot = Network.TaskAttributes & { id: TaskId };

  /**
   * What `update` writes, in the SDK's one patch vocabulary (`Patch`, as
   * `RowPatch` and `EventPatch` take it): a field the patch names is set to
   * its value, or cleared with `Patch.clear`; a field it omits is left
   * unchanged, on the server and in the local mirror. `title` is set-only
   * (the server's title is not nullable). A decoded wire body is
   * `TasksClient.patchOf(body)`.
   *
   * ```ts
   * await client.tasks.update(task, { dueDate: Patch.clear, importance: "high" }, actor);
   * ```
   */
  export type Patch = {
    readonly title?: string;
    readonly description?: Patch.Clearable<string>;
    readonly dueDate?: Patch.Clearable<DateTime<Utc>>;
    readonly eventId?: Patch.Clearable<NonNullable<Network.TaskUpdateAttributes["eventId"]>>;
    readonly importance?: Patch.Clearable<Network.TaskPriority>;
    readonly position?: Patch.Clearable<Rational>;
    readonly scheduledStart?: Patch.Clearable<DateTime<Utc>>;
    readonly scheduledEnd?: Patch.Clearable<DateTime<Utc>>;
    readonly status?: Patch.Clearable<Network.TaskStatus>;
    readonly completedAt?: Patch.Clearable<DateTime<Utc>>;
  };

  /**
   * Public API surface for consumer components and mocks.
   *
   * Every mutation takes the held task (`Item`) — never an id to re-resolve —
   * and the writing surface's `actor` (`Actor.EXTERNAL` for a caller with
   * no echo of its own to recognise); it stamps the `TasksChanged` and cell
   * invalidations the write emits. Every
   * mutation throws `TaskWriteError` when the server rejects the write or
   * does not answer; the SDK has already undone any local change by then.
   * A caller holding only an id gets the task from `get(id)` (loads the
   * task row once) or `peek(id)` (the already-loaded row, synchronous).
   */
  export interface Api {
    /**
     * Create a task (`POST /api/tasks`, which assigns the display id and
     * writes the task's cell). The server's task is mirrored into the local
     * row once the write succeeds. `task` states every field a caller
     * chooses (`TasksClient.NewTask`, `null` for one the task does not have);
     * the server-owned ones are not the caller's. Resolves with the created
     * task as `list()` / `get()` return it.
     */
    create(
      task: TasksClient.NewTask,
      actor: Actor,
    ): Promise<TasksClient.Snapshot>;
    /**
     * Write `attributes` to the held task. The SDK owns the optimistic
     * flow: the local cell moves/updates synchronously — before `update`
     * returns its promise — the PATCH is sent, and a failed PATCH puts the
     * cell back before the returned promise rejects with `TaskWriteError`.
     * A drag settle therefore needs nothing but
     * `const settled = update(...); clearGhost(); await settled;`.
     *
     * Only the changed attributes go on the wire (`PATCH /api/tasks/{id}`),
     * so two writes from the same held item never undo each other — and
     * overlapping writes never undo each other locally either: a failed
     * write takes back only its own attributes, keeping every other write's
     * (see `TasksClient` → "Overlapping writes").
     * `attributes` is a patch (`TasksClient.Patch`): `Patch.clear` clears a
     * field; omitting the key leaves it unchanged, on the server and in the
     * local mirror. `title` cannot be cleared, so the patch type has no
     * clearable title. Resolves once the server accepted the write with
     * `{ kind: "accepted", view, source }` — the client's view of the task
     * and where it came from, never a server read (`TasksClient.Updated`).
     */
    update(task: TasksClient.Item, attributes: Patch, actor: Actor): Promise<Updated>;
    /**
     * Delete the held task (`DELETE /api/tasks/{id}` — keyed by id alone, so
     * the server delete never depends on locating the cell). Its local cell
     * is cleared once the delete succeeds, and the delete wins over any
     * `update` of the task still in flight: its later answer repaints nothing.
     */
    delete(task: TasksClient.Item, actor: Actor): Promise<void>;
    list(): Promise<TasksClient.Snapshot[]>;
    /** The task with this id, loading the task row first if it is not loaded. */
    get(taskId: TaskId): Promise<Option<TasksClient.Snapshot>>;
    /** The task with this id from the already-loaded task row; `null` if the row is not loaded or has no such task. */
    peek(taskId: TaskId): Option<TasksClient.Snapshot>;
  }
}

/**
 * A task's fields as its pending-write model holds them: the confirmed base
 * (every field, captured from the view when a write first touches it) and
 * each `update`'s patch alike.
 */
type TaskFields = Partial<Network.TaskAttributes>;

/**
 * Where one task's cell sits while it has writes in flight: the cell the
 * row stored when the first write was issued (`origin`, `originDate`) — a
 * stored date wins over re-deriving it while the anchor is unchanged — and
 * where the mirror shows it now (`shownDate`; `null`: nowhere).
 */
type TaskPlacement = {
  readonly origin: TasksClient.Snapshot;
  readonly originDate: Option<NaiveDate.Partial>;
  shownDate: Option<NaiveDate.Partial>;
};

/**
 * Global tasks client backed by the canonical task row.
 *
 * Reads scan the row handle's cells directly, yielding any cell value
 * with an `id` field as a `TasksClient.Snapshot`. Creates POST /api/tasks (display id
 * assignment + cell write); updates PATCH /api/tasks/{id} with only the
 * changed attributes (the server merges them into the stored cell);
 * deletes DELETE /api/tasks/{id}. Every write is mirrored into the
 * row-keyed local-edit chokepoint so surfaces repaint without a refetch —
 * optimistically (with rollback) for `update`, after the ack otherwise.
 *
 * ## Overlapping writes
 *
 * `update` does not wait for an earlier `update` of the same task, so the
 * client keeps, per task, a confirmed base plus the writes still in flight,
 * and the mirror is always the base with the surviving writes replayed in
 * issue order. The server is assumed to apply PATCHes in the order they
 * were issued — the order the optimistic mirror already shows them in.
 *
 * - A write that succeeds is folded into the base once every older write
 *   has settled (acknowledged writes behind an in-flight one stay replayed
 *   until then), so two successes in either completion order leave the
 *   later-issued value of each field.
 * - A write that fails is dropped and the mirror re-projected: only its own
 *   attributes are taken back; every other write — older or newer, in
 *   flight or acknowledged — stays applied.
 * - A cell move is part of the projection: the cell sits wherever the base
 *   plus the surviving writes anchor it, so a failed date move puts the
 *   cell back under the other writes' fields, and a failed rename leaves a
 *   successful move where it landed.
 * - Delete wins. Once `delete` succeeds, the task's pending writes are
 *   forgotten: a later PATCH answer repaints nothing (its promise still
 *   resolves, or rejects with `TaskWriteError`), and an `update` issued
 *   after the delete sends its PATCH without touching the mirror.
 * - While a task has writes in flight, the mirror is projected from the
 *   base, not from row data that arrives meanwhile.
 */
export class TasksClient implements TasksClient.Api {
  #rowPromise: Option<Promise<Option<SheetRow.Handle>>> = null;
  #cachedRow: Option<SheetRow.Handle> = null;
  #indexPromise: Option<Promise<void>> = null;
  /**
   * Per task id: the `update`s in flight over the task's confirmed fields —
   * the task instance of the one ledger every row, sheet and link update
   * shares (`Ledger`, a pure model: the mirror shows its projection; see
   * "Overlapping writes").
   */
  readonly #writes = new Ledger<TaskWrites>(TASK_FIELDS, {
    sinks: () => [],
    name: "tasks-update",
    takeBackContext: "the take-back of a refused task update",
    describe: (task) => ({ task }),
    live: () => !this.#lifecycle.disposed,
    abandoned: (task, fields, error) =>
      Tracing.warn("net")("tasks-update/take-back-abandoned", {
        task,
        fields: [...fields].join(","),
        error: error.message,
      }),
  });
  /** Per task id with writes in flight: where its cell sits (`TaskPlacement`). */
  readonly #placements = new Map<string, TaskPlacement>();
  /** Tasks this client deleted: no `update` answer may repaint them. */
  readonly #deleted = new Set<string>();

  /** The owning client's liveness: every network verb checks it first. */
  readonly #lifecycle: ClientLifecycle;

  /** `lifecycle`: the owning client's (`LonaSdk.Client.Lifecycle`); after
   *  its `dispose` every network verb (`list`, `get`, `create`, `update`,
   *  `delete`) rejects with `LonaSdk.Client.DisposedError` before any
   *  local change. */
  readonly #invalidation: InvalidationSink;
  readonly #api: TypedApi<Network.Routes>;
  readonly #rows: RowsClient.Opener;
  readonly #cells: TasksClient.CellSink;

  /**
   * `rows` opens the canonical task row (`:~lona:tasks`; the client's
   * `RowsClient`) and `cells` paints its task cells (the client's cell data
   * source): each is a structural seam, so a test hands in a double that
   * `satisfies` it.
   */
  constructor(
    api: TypedApi<Network.Routes>,
    rows: RowsClient.Opener,
    invalidation: InvalidationSink,
    cells: TasksClient.CellSink,
    lifecycle: ClientLifecycle,
  ) {
    this.#api = api;
    this.#rows = rows;
    this.#invalidation = invalidation;
    this.#cells = cells;
    this.#lifecycle = lifecycle;
  }

  /**
   * Get or open the canonical task row. Cached once it opens; a missing row
   * (`null`) or a failed open is not remembered, so the next call retries.
   */
  #maybeTaskRow(): Promise<Option<SheetRow.Handle>> {
    if (!this.#rowPromise) {
      const opening = this.#rows.open(RowKey.of(TASK_ROW_KEY)).then((row) => {
        if (row) this.#cachedRow = row;
        else if (this.#rowPromise === opening) this.#rowPromise = null;
        return row;
      });
      this.#rowPromise = opening;
      opening.catch(() => {
        if (this.#rowPromise === opening) this.#rowPromise = null;
      });
    }
    return this.#rowPromise;
  }

  /**
   * Open the task row after a write the server acknowledged. Never throws:
   * the write stands, so a failed open is logged (and retried by the next
   * read — `maybeTaskRow` does not cache it) instead of turning a landed
   * write into a rejection that is not a `TaskWriteError`.
   */
  async #openTaskRowAfterAck(
    step: TaskWriteError.Step,
    taskId: string,
  ): Promise<void> {
    try {
      await this.#maybeTaskRow();
    } catch (err) {
      Tracing.warn("net")("tasks-client/row-open-after-ack failed", {
        step,
        taskId,
        error: String(err),
      });
    }
  }

  async #taskRow(): Promise<SheetRow.Handle> {
    const row = await this.#maybeTaskRow();
    if (!row) throw new Error(`${TASK_ROW_KEY} row not found`);
    return row;
  }

  /**
   * Ensure the RowCellCache exists and cells have been fetched at least once.
   * A failed fetch is not remembered: the in-flight promise is dropped so
   * the next read retries, and this read throws an `SdkRequestError`
   * (boundary `"cells"`, step `"get-cells"`: the request is the task row's
   * cell read) rather than serving an empty list as if it were real.
   */
  #ensureIndex(): Promise<void> {
    if (!this.#indexPromise) {
      const loading = this.#loadIndex();
      this.#indexPromise = loading;
      loading.catch(() => {
        if (this.#indexPromise === loading) this.#indexPromise = null;
      });
    }
    return this.#indexPromise;
  }

  async #loadIndex(): Promise<void> {
    const row = await this.#taskRow();
    const handle = rowHandleOf(row);
    // Fetch the canonical row's cells directly and feed the
    // (registry-shared) handle. Going through `fetcher.fetchRange`
    // would route the decade-wide TASK_FETCH_RANGE through the
    // sheet accessor's `fetchFn` override when a sheet sharing
    // this handle is open — awaiting a sheet-wide bulk fetch of
    // the whole range. The direct row GET matches what the
    // standalone fetchFn did before handles were shared.
    const path = "/api/rows/{rowKey}/cells";
    const cells = await SdkRequestError.checkRequest("cells", "get-cells", "GET", path, () =>
      this.#api.getResult(path, {
        path: { rowKey: RowNetworkKey.forRead(RowNetworkKey.fromWire(TASK_ROW_KEY)) },
        query: {
          start: TASK_FETCH_RANGE.start.date,
          end: TASK_FETCH_RANGE.end.date,
          mock_sync_delay: null,
          booking_id: null,
        },
      }));
    handle.receiveBatchData(TASK_FETCH_RANGE, cells);
  }

  *#iterTasks(): Iterable<TasksClient.Snapshot> {
    const rh = this.#cachedRow == null ? null : rowHandleOf(this.#cachedRow);
    if (!rh) return;
    for (const cell of rh.allCells()) yield* this.#tasksFromCell(cell.value);
  }

  *#tasksFromCell(raw: Option<CellValue>): Iterable<TasksClient.Snapshot> {
    if (raw != null && typeof raw === "object" && !Array.isArray(raw) && isTaskSnapshot(raw)) {
      yield raw;
    }
  }

  /** Compute the cell date for a task. */
  #cellDate(task: TasksClient.Item): Option<NaiveDate.Partial> {
    const date = task.scheduledStart ?? task.dueDate ?? task.createdAt;
    if (!date) return null;
    return NaiveDate.Partial.fromDate(date.ndt.date);
  }

  // ── Reads (index) ──────────────────────────────────────────────

  async list(): Promise<TasksClient.Snapshot[]> {
    this.#lifecycle.assertLive("tasks.list");
    await this.#ensureIndex();
    return [...this.#iterTasks()];
  }

  async get(taskId: TaskId): Promise<Option<TasksClient.Snapshot>> {
    this.#lifecycle.assertLive("tasks.get");
    requireNonEmptyKey("TasksClient.get", "taskId", taskId);
    await this.#ensureIndex();
    return this.#loadedTask(taskId);
  }

  peek(taskId: TaskId): Option<TasksClient.Snapshot> {
    requireNonEmptyKey("TasksClient.peek", "taskId", taskId);
    return this.#loadedTask(taskId);
  }

  // ── Writes ─────────────────────────────────────────────────────

  /**
   * Create a new task from `task` (`TasksClient.NewTask`: the caller's
   * fields; the server-owned ones are never sent). `POST /api/tasks` assigns
   * the display id and writes the task's cell server-side; the returned task
   * is then mirrored into the local row. Throws `TaskWriteError` (nothing
   * mirrored) on failure — outcome `"unaddressable"`, nothing sent, for a
   * task with no string `title`.
   */
  async create(
    task: TasksClient.NewTask,
    actor: Actor,
  ): Promise<TasksClient.Snapshot> {
    this.#lifecycle.assertLive("tasks.create");
    // A caller outside the type system can hand in anything.
    const title: Option<string> = task.title ?? null;
    if (typeof title !== "string") {
      throw new TaskWriteError({
        step: "create",
        method: "POST",
        path: "/api/tasks",
        failure: { outcome: "unaddressable" },
        details: "tasks.create: a new task needs a `title`",
      });
    }
    const body = TasksClient.NewTask.encode(task);
    let created: Network.Task;
    try {
      created = await TaskWriteError.check(
        "create",
        "POST",
        "/api/tasks",
        () => this.#api.postResult("/api/tasks", { body }),
      );
    } catch (err) {
      // No verdict (or one the SDK could not read): the task may be on the
      // server, under an id the server minted — re-read the task row so it
      // comes back from there.
      afterFailedWrite(err, this.#lifecycle, "tasks.create", () => this.#rereadTaskRow());
      throw err;
    }
    const date = this.#cellDate(created);
    if (date) this.#applyLocal(date, created, created.id.toString(), actor);
    this.#notifyChanged(actor);
    return created;
  }

  /**
   * Write `attributes` to the held task (any `TasksClient.Item`: a
   * `Network.Task`, a `TasksClient.Snapshot` from `list()` / `get()` / `peek()`, or a
   * renderer's rows-side `TaskCellValue`).
   *
   * Optimistic: before the first `await`, the local cell is moved/merged
   * (onto the task's pending base — the row's current copy of the task,
   * else the held item — with the writes still in flight) and
   * `TasksChanged` fires — so the change paints in the frame the caller
   * issued it. The wire carries only `attributes`, keyed by the held task's
   * id — the server merges them into the stored cell — so a later write
   * from the same held item cannot undo an earlier one. On success the
   * write is folded into the task's base and the mirror re-applied
   * (idempotent, id-keyed); on failure only this write is taken back —
   * other writes of the task stay applied (see "Overlapping writes") — and
   * `TaskWriteError` is thrown.
   *
   * Clearing vs leaving a field: `Patch.clear` clears it
   * (`{ importance: Patch.clear }` removes the priority); omitting the key
   * leaves it unchanged. The wire body and the local mirror are both the
   * patch's one encoding (`TasksClient.patchBody`: a cleared field is
   * `null`, an omitted one absent), so the mirror never shows a clear the
   * server did not receive. `title` is not nullable on the server, and
   * `TasksClient.Patch` has no clearable title.
   *
   * Resolves once the server accepted the write with a
   * `TasksClient.Updated` — `{ kind: "accepted", view, source }`. The PATCH
   * answers no body, so `view` is the CLIENT's view of the accepted write,
   * never a server read, and `source` says which: `"row"` — the task row's
   * copy (this write folded in, with any other write of the task still in
   * flight replayed over it); `"merged"` — the row could not be opened or
   * no longer holds the task, so `view` is the held task with `attributes`
   * merged (`TasksClient.merge`). A caller that needs the stored task reads
   * it (`get`) — `source: "merged"` is the cue.
   */
  async update(
    task: TasksClient.Item,
    attributes: TasksClient.Patch,
    actor: Actor,
  ): Promise<TasksClient.Updated> {
    this.#lifecycle.assertLive("tasks.update");
    const taskId = task.id;
    requireNonEmptyKey("TasksClient.update", "task.id", taskId);
    // Synchronous optimistic apply — no `await` may precede it. A task this
    // client deleted is not repainted (delete wins).
    const write = this.#writes.begin(taskId, TasksClient.patchBody(attributes), actor, {
      // A pure model announces nothing: the correction is the mirror's own (`TasksChanged`, no actor).
      correction: Actor.EXTERNAL,
      quiet: false,
      capture: () => this.#viewOf(task),
    });
    if (!this.#deleted.has(taskId)) {
      this.#placementFor(task);
      this.#projectView(taskId, this.#shownOf(task, this.#writes.project(taskId)), actor);
      this.#notifyChanged(actor);
    }
    try {
      // The body is the ledger's: this write's fields of the projection at
      // send time (its own patch, sent at once).
      await this.#writes.send(write, (body) =>
        TaskWriteError.check(
          "update",
          "PATCH",
          "/api/tasks/{taskId}",
          () => this.#api.patchResult("/api/tasks/{taskId}", { path: { taskId }, body: taskUpdateBody(body) }),
        ));
    } catch (err) {
      // The client's own actor, not the writer's: the issuing surface must
      // repaint the take-back.
      if (this.#settle(task, write, { accepted: false, error: err, context: "tasks.update" }, null)) {
        this.#notifyChanged(null);
      }
      // No verdict (or one the SDK could not read): the PATCH may have
      // landed — re-read the task, so what the server holds comes back.
      afterFailedWrite(err, this.#lifecycle, "tasks.update", () => this.#rereadTask(task));
      throw err;
    }
    if (this.#settle(task, write, Ledger.ACCEPTED, actor)) this.#notifyChanged(actor);
    // Open (not fetch) the task row, so the next write's base is the row's
    // copy of the task rather than an older held item. The server already
    // accepted the write, so a failed open must not reject it.
    await this.#openTaskRowAfterAck("update", taskId);
    const fromRow = this.#loadedTask(taskId);
    if (fromRow != null) return { kind: "accepted", view: fromRow, source: "row" };
    const held: TasksClient.Snapshot = { ...taskAttributesOf(task), id: taskId };
    return {
      kind: "accepted",
      view: { ...TasksClient.merge(held, attributes), id: taskId },
      source: "merged",
    };
  }

  /**
   * Delete the held task (any `TasksClient.Item`) by id —
   * `DELETE /api/tasks/{taskId}` needs no cell date. Once the server has
   * deleted it, its local cell is cleared: located from the loaded row,
   * else from the held task's dates — never re-fetched. A held task with
   * no locatable cell has nothing local to clear. Throws `TaskWriteError`
   * (nothing cleared) on failure, and only then: once the server deleted
   * the task the call resolves, even if the task row cannot be opened.
   *
   * Delete wins over the task's `update`s still in flight: their later
   * answers repaint nothing (see "Overlapping writes").
   */
  async delete(task: TasksClient.Item, actor: Actor): Promise<void> {
    this.#lifecycle.assertLive("tasks.delete");

    const taskId = task.id;
    requireNonEmptyKey("TasksClient.delete", "task.id", taskId);
    try {
      await TaskWriteError.check(
        "delete",
        "DELETE",
        "/api/tasks/{taskId}",
        () => this.#api.delResult("/api/tasks/{taskId}", { path: { taskId } }),
      );
    } catch (err) {
      // Nothing was cleared; the DELETE may have landed — re-read the task
      // (a task the server no longer holds is cleared then).
      afterFailedWrite(err, this.#lifecycle, "tasks.delete", () => this.#rereadTask(task));
      throw err;
    }
    // Synchronously with the ack: no pending write may repaint it after this.
    const placement = this.#placements.get(taskId);
    this.#placements.delete(taskId);
    this.#deleted.add(taskId);
    // Locates the cell to clear; the delete already landed, so a failed open
    // falls back to the held task's dates instead of rejecting it.
    await this.#openTaskRowAfterAck("delete", taskId);
    const date = placement?.shownDate ?? this.#loadedCellDate(taskId) ??
      this.#cellDate(task);
    if (date) this.#applyLocal(date, null, taskId, actor);
    this.#notifyChanged(actor);
  }

  /**
   * A failed `create`'s recovery (`afterFailedWrite`): the server mints a
   * new task's id, so the task cannot be re-read by it — the task row's
   * cells are read again (the index `list` reads) and `TasksChanged`
   * announced as the client's own actor, so a task the unanswered create
   * stored shows. Nothing once the client was disposed.
   */
  async #rereadTaskRow(): Promise<void> {
    if (this.#lifecycle.disposed) return;
    this.#indexPromise = null;
    await this.#ensureIndex();
    if (this.#lifecycle.disposed) return;
    this.#notifyChanged(null);
  }

  /**
   * A failed `update` / `delete`'s recovery (`afterFailedWrite`): the task
   * re-read (`GET /api/tasks/{taskId}`) through its pending-write model —
   * the server's task becomes its confirmed state and every write of it
   * still in flight stays applied over it — and shown in the mirror,
   * announced as the client's own actor. A task the server no longer holds
   * (404) is cleared from the mirror, as a landed `delete` clears it. A
   * task this client deleted is not repainted (delete wins). An answer
   * that arrives after the client was disposed is dropped: nothing shown.
   */
  async #rereadTask(task: TasksClient.Item): Promise<void> {
    const taskId = task.id;
    const read = this.#writes.beginRead(taskId);
    let fresh: Option<Network.Task>;
    try {
      fresh = await SdkRequestError.checkRequest(
        "tasks",
        "get",
        "GET",
        "/api/tasks/{taskId}",
        () => SdkRequestError.notFoundAsNull(() => this.#api.getResult("/api/tasks/{taskId}", { path: { taskId } })),
      );
    } catch (err) {
      // A failed read changes nothing (`afterFailedWrite` traces it).
      // after-failed-write: the read closes unanswered — nothing was written, nothing is taken back.
      this.#writes.settleRead(read, null);
      throw err;
    }
    if (this.#lifecycle.disposed) {
      this.#writes.settleRead(read, null);
      return;
    }
    if (fresh == null) {
      this.#writes.settleRead(read, null);
      const placement = this.#placements.get(taskId);
      this.#placements.delete(taskId);
      this.#deleted.add(taskId);
      const date = placement?.shownDate ?? this.#loadedCellDate(taskId) ?? this.#cellDate(task);
      if (date) this.#applyLocal(date, null, taskId, null);
      this.#notifyChanged(null);
      return;
    }
    const reread = this.#writes.settleRead(read, taskAttributesOf(fresh));
    if (reread == null || this.#deleted.has(taskId)) return;
    if (this.#placements.has(taskId)) {
      this.#projectView(taskId, reread, null);
    } else {
      // No write in flight: placed as a write's projection is — from the
      // cell the task sits in now, moved when the recovered anchor differs.
      this.#showIn(this.#placementNow(task), taskId, reread, null);
    }
    this.#notifyChanged(null);
  }

  /**
   * The task as the view shows it now — the row's current copy (so an
   * earlier local edit is kept), else the held item — as the fields its
   * pending-write model captures.
   */
  #viewOf(task: TasksClient.Item): TaskFields {
    return this.#loadedTask(task.id) ?? taskAttributesOf(task);
  }

  /** The task's placement, started when none of its writes is in flight. */
  #placementFor(task: TasksClient.Item): TaskPlacement {
    const existing = this.#placements.get(task.id);
    if (existing) return existing;
    const placement = this.#placementNow(task);
    this.#placements.set(task.id, placement);
    return placement;
  }

  /**
   * Where the task's cell sits now: the row's copy at the date the row
   * stores it, else the held item at its anchor date.
   */
  #placementNow(task: TasksClient.Item): TaskPlacement {
    const origin: TasksClient.Snapshot = this.#loadedTask(task.id) ??
      { ...taskAttributesOf(task), id: task.id };
    const originDate = this.#loadedCellDate(task.id) ?? this.#cellDate(origin);
    return { origin, originDate, shownDate: originDate };
  }

  /**
   * Settle one write in the task's ledger on its `verdict` (`Ledger.settle`:
   * a landed write folds in issue order, a refused one is dropped alone) and
   * re-project the mirror. Returns `false` (and repaints nothing) when the
   * task was deleted meanwhile — delete wins.
   */
  #settle(
    task: TasksClient.Item,
    write: Ledger.Write<TaskWrites>,
    verdict: Ledger.Verdict,
    actor: Option<Actor>,
  ): boolean {
    const taskId = task.id;
    const settled = this.#writes.settle(write, verdict);
    if (this.#deleted.has(taskId)) return false;
    if (!this.#placements.has(taskId)) return false;
    this.#projectView(taskId, this.#shownOf(task, settled.projection), actor);
    if (!this.#writes.writing(taskId)) this.#placements.delete(taskId);
    return true;
  }

  /**
   * The task as the mirror shows it: its view now (`#viewOf`) with the
   * ledger's `projection` over it — the fields a write touched at their
   * confirmed value with every write still standing replayed in issue order.
   */
  #shownOf(task: TasksClient.Item, projection: Option<TaskFields>): TaskFields {
    return { ...this.#viewOf(task), ...projection };
  }

  /**
   * Write `view` (the task's projection) into the local cell, moving the
   * cell when the projection is anchored elsewhere than it is shown.
   */
  #projectView(taskId: TaskId, view: TaskFields, actor: Option<Actor>): void {
    const placement = this.#placements.get(taskId);
    if (placement == null) return;
    this.#showIn(placement, taskId, view, actor);
  }

  /**
   * Show `view` in the mirror from `placement`: at the date `#placeOf`
   * puts it, clearing the cell it was shown at when that differs.
   */
  #showIn(placement: TaskPlacement, taskId: TaskId, view: TaskFields, actor: Option<Actor>): void {
    const projected: TasksClient.Snapshot = {
      ...taskAttributesOf(view),
      id: taskId,
    };
    const date = this.#placeOf(placement, projected);
    const shown = placement.shownDate;
    if (shown && (!date || shown.toString() !== date.toString())) {
      this.#applyLocal(shown, null, taskId, actor);
    }
    if (date) this.#applyLocal(date, projected, taskId, actor);
    placement.shownDate = date;
  }

  /**
   * Where `task` — the projection — is stored: where the row stored it
   * when the first write was issued while the writes leave its anchor date
   * alone (a cell's stored date wins over re-deriving it), else at the new
   * anchor, else where it was stored. `null` when neither has a locatable
   * date.
   */
  #placeOf(
    placement: TaskPlacement,
    task: TasksClient.Snapshot,
  ): Option<NaiveDate.Partial> {
    const anchor = this.#cellDate(task);
    const originAnchor = this.#cellDate(placement.origin);
    if (anchor?.toString() === originAnchor?.toString()) return placement.originDate;
    return anchor ?? placement.originDate;
  }

  /** The loaded row's copy of a task, by id. */
  #loadedTask(taskId: string): Option<TasksClient.Snapshot> {
    for (const task of this.#iterTasks()) {
      if (task.id.toString() === taskId) return task;
    }
    return null;
  }

  /** Stored date for a loaded task cell, resolved from row data rather than task fields. */
  #loadedCellDate(taskId: string): Option<NaiveDate.Partial> {
    const rh = this.#cachedRow == null ? null : rowHandleOf(this.#cachedRow);
    if (!rh) return null;
    for (const cell of rh.allCells()) {
      for (const task of this.#tasksFromCell(cell.value)) {
        if (task.id.toString() !== taskId) continue;
        return cell.kind === "date"
          ? cell.date
          : NaiveDate.Partial.fromDate(cell.dtr.start.date);
      }
    }
    return null;
  }

  // ── Local mirror ─────────────────────────────────────────────────

  /**
   * Route the local edit through the row-keyed chokepoint so every
   * surface rendering the canonical tasks row (facades, registry
   * handle, legends) updates without a refetch. The chokepoint covers
   * this client's own handle too — it is the same registry handle.
   */
  #applyLocal(
    date: NaiveDate.Partial,
    data: Option<TasksClient.Snapshot>,
    taskId: string,
    actor: Option<Actor>,
  ): void {
    // A take-back (no writer) is stamped with the client's own actor, as its
    // `TasksChanged` is: the issuing surface repaints it.
    const stamp = actor ?? TASKS_ACTOR;
    if (data == null) this.#cells.clearCell(RowNetworkKey.fromWire(TASK_ROW_KEY), date, CellId.of(taskId), stamp);
    else this.#cells.writeCell(RowNetworkKey.fromWire(TASK_ROW_KEY), date, data, CellId.of(taskId), stamp);
  }

  #notifyChanged(actor: Option<Actor>): void {
    this.#invalidation.emit(new TasksChanged(actor ?? TASKS_ACTOR));
  }
}

export namespace TasksClient {
  export namespace Item {
    /**
     * The held task a rows-side task cell (`TaskCellValue`, as a calendar
     * renderer holds it) names: its id and dates, each unset one `null` (a
     * cell carries no `createdAt`).
     */
    export function ofCell(cell: TaskCellValue): Item {
      return {
        id: cell.id,
        scheduledStart: cell.scheduledStart ?? null,
        dueDate: cell.dueDate ?? null,
        createdAt: null,
        completedAt: cell.completedAt ?? null,
      };
    }
  }

  export namespace NewTask {
    /** The server-owned task fields: `POST /api/tasks` assigns them. */
    export const SERVER_OWNED: readonly ("displayId" | "displayNumber" | "createdAt" | "completedAt")[] = deepFrozen([
      "displayId",
      "displayNumber",
      "createdAt",
      "completedAt",
    ]);

    /**
     * The `POST /api/tasks` body for `task`: its fields, and `null` for
     * every server-owned one. A fresh object per call.
     */
    export function encode(task: NewTask): Network.TaskAttributes {
      return Network.TaskAttributes.make({
        title: task.title,
        description: task.description,
        dueDate: task.dueDate,
        eventId: task.eventId,
        importance: task.importance,
        position: task.position,
        scheduledStart: task.scheduledStart,
        scheduledEnd: task.scheduledEnd,
        status: task.status,
        displayId: null,
        displayNumber: null,
        createdAt: null,
        completedAt: null,
      });
    }

    /**
     * The new task a decoded wire body describes (a command line's or a tool
     * argument's `Network.TaskAttributes`). Throws `TypeError` when it has no
     * `title`, or sets a server-owned field (`SERVER_OWNED`) — a create
     * cannot choose those.
     */
    export function fromWire(attributes: Network.TaskAttributes): NewTask {
      const owned = SERVER_OWNED.filter((field) => attributes[field] != null);
      if (owned.length > 0) {
        throw new TypeError(`TasksClient.NewTask.fromWire: ${owned.join(", ")} ${owned.length === 1 ? "is" : "are"} assigned by the server`);
      }
      if (attributes.title == null) {
        throw new TypeError("TasksClient.NewTask.fromWire: a new task needs a `title`");
      }
      return {
        title: attributes.title,
        description: attributes.description,
        dueDate: attributes.dueDate,
        eventId: attributes.eventId,
        importance: attributes.importance,
        position: attributes.position,
        scheduledStart: attributes.scheduledStart,
        scheduledEnd: attributes.scheduledEnd,
        status: attributes.status,
      };
    }
  }
}

export namespace TasksClient {
  /**
   * Where a tasks client paints the task row's cells locally (keyed by the
   * row's key and the task's `CellId`, stamped with the writing surface):
   * the local-write verbs every local seam spells (`writeCell` /
   * `clearCell`, as `platformOf(handle).local`'s), `actor` required. The
   * client routes them to its cell data source; a test passes a recorder.
   */
  export interface CellSink {
    writeCell(
      rowKey: RowNetworkKey,
      date: NaiveDate.Partial,
      data: TasksClient.Snapshot,
      cellId: Option<CellId>,
      actor: Actor,
    ): void;
    clearCell(
      rowKey: RowNetworkKey,
      date: NaiveDate.Partial,
      cellId: Option<CellId>,
      actor: Actor,
    ): void;
  }

  /**
   * What `update` resolves once the server accepted the write: the
   * client's `view` of the task (the PATCH answers no body — this is never
   * a server read) and its `source` — `"row"`: the task row's copy, pending
   * writes replayed; `"merged"`: the row could not be opened or no longer
   * holds the task, so the held task with the patch merged.
   */
  export type Updated = {
    readonly kind: "accepted";
    readonly view: TasksClient.Snapshot;
    readonly source: "row" | "merged";
  };

  /**
   * A decoded `PATCH /api/tasks/{id}` body (`Network.TaskUpdateAttributes`,
   * e.g. a tool's JSON input) as a `Patch`: a `null` field is `Patch.clear`,
   * an absent one is left out. Throws `TypeError` when it clears the title
   * (`title: null`): the server's title cannot be cleared, and the write
   * would otherwise leave it unchanged without saying so.
   */
  export function patchOf(attributes: Network.TaskUpdateAttributes): Patch {
    if (attributes.title === null) {
      throw new TypeError("TasksClient.patchOf: a task's title cannot be cleared (title: null)");
    }
    const has = (key: keyof Network.TaskUpdateAttributes): boolean =>
      Object.hasOwn(attributes, key) && attributes[key] !== undefined;
    return {
      ...(attributes.title != null ? { title: attributes.title } : {}),
      ...(has("description") ? { description: Patch.fromWire(attributes.description ?? null) } : {}),
      ...(has("dueDate") ? { dueDate: Patch.fromWire(attributes.dueDate ?? null) } : {}),
      ...(has("eventId") ? { eventId: Patch.fromWire(attributes.eventId ?? null) } : {}),
      ...(has("importance") ? { importance: Patch.fromWire(attributes.importance ?? null) } : {}),
      ...(has("position") ? { position: Patch.fromWire(attributes.position ?? null) } : {}),
      ...(has("scheduledStart") ? { scheduledStart: Patch.fromWire(attributes.scheduledStart ?? null) } : {}),
      ...(has("scheduledEnd") ? { scheduledEnd: Patch.fromWire(attributes.scheduledEnd ?? null) } : {}),
      ...(has("status") ? { status: Patch.fromWire(attributes.status ?? null) } : {}),
      ...(has("completedAt") ? { completedAt: Patch.fromWire(attributes.completedAt ?? null) } : {}),
    };
  }

  /**
   * The wire body of `PATCH /api/tasks/{id}` — `patch`'s one encoding: a
   * set field carries its value, a cleared one (`Patch.clear`) `null`, an
   * omitted one no key. The server reads a present key as a write (`null`
   * clears) and an absent key as "leave unchanged". A fresh object on every
   * call.
   */
  export function patchBody(patch: Patch): Network.TaskUpdateAttributes {
    const body: Network.TaskUpdateAttributes = {};
    if (patch.title !== undefined) body.title = patch.title;
    if (patch.description !== undefined) body.description = Patch.wire(patch.description);
    if (patch.dueDate !== undefined) body.dueDate = Patch.wire(patch.dueDate);
    if (patch.eventId !== undefined) body.eventId = Patch.wire(patch.eventId);
    if (patch.importance !== undefined) body.importance = Patch.wire(patch.importance);
    if (patch.position !== undefined) body.position = Patch.wire(patch.position);
    if (patch.scheduledStart !== undefined) body.scheduledStart = Patch.wire(patch.scheduledStart);
    if (patch.scheduledEnd !== undefined) body.scheduledEnd = Patch.wire(patch.scheduledEnd);
    if (patch.status !== undefined) body.status = Patch.wire(patch.status);
    if (patch.completedAt !== undefined) body.completedAt = Patch.wire(patch.completedAt);
    return body;
  }

  /**
   * The local image of `PATCH /api/tasks/{id}`, applying the same rule as
   * the server (`apply_update` in lona-service `task_client.rs`) to the
   * patch's wire body (`patchBody`): each field the patch names replaces
   * the task's field — `Patch.clear` clears — and an omitted one is left
   * as it is. A non-null `status`
   * re-derives `completedAt` as the server does — set when completed,
   * cleared otherwise (the server stamps its own clock; this keeps a stamp
   * the patch or task already carries). The result is a new snapshot; the
   * held task is not mutated.
   */
  export function merge(
    task: TasksClient.Snapshot,
    attributes: Patch,
  ): TasksClient.Snapshot {
    const patch = patchBody(attributes);
    const merged: TasksClient.Snapshot = { ...task };
    if (patch.title != null) merged.title = patch.title;
    if (patch.description !== undefined) merged.description = patch.description;
    if (patch.dueDate !== undefined) merged.dueDate = patch.dueDate;
    if (patch.eventId !== undefined) merged.eventId = patch.eventId;
    if (patch.importance !== undefined) merged.importance = patch.importance;
    if (patch.position !== undefined) merged.position = patch.position;
    if (patch.scheduledStart !== undefined) merged.scheduledStart = patch.scheduledStart;
    if (patch.scheduledEnd !== undefined) merged.scheduledEnd = patch.scheduledEnd;
    if (patch.status !== undefined) merged.status = patch.status;
    if (patch.completedAt !== undefined) merged.completedAt = patch.completedAt;
    if (patch.status != null) {
      merged.completedAt = patch.status === "completed"
        ? (patch.completedAt ?? task.completedAt ?? Utc.now())
        : null;
    }
    return merged;
  }
}

/** The fields a task `update` writes (the server's `PATCH` body keys). */
const TASK_UPDATE_FIELDS = [
  "title",
  "description",
  "dueDate",
  "eventId",
  "importance",
  "position",
  "scheduledStart",
  "scheduledEnd",
  "status",
  "completedAt",
] as const;
type TaskUpdateField = (typeof TASK_UPDATE_FIELDS)[number];

/** Copy `from[field]` onto `to`, one typed field at a time (an absent one stays absent). */
function copyTaskField(to: TaskFields, from: TaskFields, field: TaskUpdateField): void {
  switch (field) {
    case "title": if (from.title !== undefined) to.title = from.title; return;
    case "description": if (from.description !== undefined) to.description = from.description; return;
    case "dueDate": if (from.dueDate !== undefined) to.dueDate = from.dueDate; return;
    case "eventId": if (from.eventId !== undefined) to.eventId = from.eventId; return;
    case "importance": if (from.importance !== undefined) to.importance = from.importance; return;
    case "position": if (from.position !== undefined) to.position = from.position; return;
    case "scheduledStart": if (from.scheduledStart !== undefined) to.scheduledStart = from.scheduledStart; return;
    case "scheduledEnd": if (from.scheduledEnd !== undefined) to.scheduledEnd = from.scheduledEnd; return;
    case "status": if (from.status !== undefined) to.status = from.status; return;
    case "completedAt": if (from.completedAt !== undefined) to.completedAt = from.completedAt; return;
  }
}

/**
 * A task ledger field: an update field, or the `completedAt` a `status`
 * write re-derives without naming it (`DERIVED_COMPLETED_AT`) — taken back
 * with the write, never sent (the server re-derives it too).
 */
type TaskField = TaskUpdateField | typeof DERIVED_COMPLETED_AT;
const DERIVED_COMPLETED_AT = "completedAt (derived)";

/** The type bundle of the task instance of `Ledger` (keyed by task id). */
interface TaskWrites extends Ledger.Types {
  readonly key: string;
  readonly change: TaskFields;
  readonly fields: TaskFields;
  readonly field: TaskField;
  readonly delta: TaskFields;
  readonly answer: unknown;
  readonly error: Error;
}

/** The update field a ledger field stands for (`completedAt` for the derived one). */
function updateFieldOf(field: TaskField): TaskUpdateField {
  return field === DERIVED_COMPLETED_AT ? "completedAt" : field;
}

/**
 * A task PATCH's ledger strategy: `merge` is the server's `apply_update`
 * (`TasksClient.merge`: a present key replaces, `null` clears, a `null`
 * title is ignored, a `status` re-derives `completedAt`); a write's fields
 * are the keys it names, plus the `completedAt` a `status` re-derives
 * (taken back with it, never sent); a body carries each named field (`null`
 * when cleared). A pure model: `TasksClient` shows the projection itself.
 */
const TASK_FIELDS: Ledger.Strategy<TaskWrites> = {
  empty: () => ({}),
  merge: (target, patch) => {
    const merged: TaskFields = { ...target };
    for (const field of TASK_UPDATE_FIELDS) {
      if (field === "title") {
        if (patch.title != null) merged.title = patch.title;
      } else if (patch[field] !== undefined) {
        copyTaskField(merged, patch, field);
      }
    }
    if (patch.status != null) {
      merged.completedAt = patch.status === "completed"
        ? (patch.completedAt ?? target.completedAt ?? Utc.now())
        : null;
    }
    return merged;
  },
  fieldsOf: (patch) => {
    const fields = new Set<TaskField>();
    for (const key of Object.keys(patch)) {
      const field = TASK_UPDATE_FIELDS.find((f) => f === key);
      if (field != null && patch[field] !== undefined) fields.add(field);
    }
    if (fields.has("status") && !fields.has("completedAt")) fields.add(DERIVED_COMPLETED_AT);
    return fields;
  },
  body: (fields, only) => {
    const body: TaskFields = {};
    for (const field of only) {
      if (field === DERIVED_COMPLETED_AT) continue;
      copyTaskFieldOrNull(body, fields, field);
    }
    return body;
  },
  adopt: (fields, from, only) => {
    const out: TaskFields = { ...fields };
    for (const field of only) copyTaskFieldOrNull(out, from, updateFieldOf(field));
    return out;
  },
  overlay: (under, over) => ({ ...under, ...over }),
  takeBack: () => ({}),
  delta: () => null,
  events: () => Ledger.SILENT,
  deferred: () => {
    throw new Error("tasks-update: the task ledger defers no announcement");
  },
  wrap: (err) => (err instanceof Error ? err : new Error(String(err))),
};

/** `to[field]` set to `from[field]`, `null` when `from` lacks it. */
function copyTaskFieldOrNull(to: TaskFields, from: TaskFields, field: TaskUpdateField): void {
  copyTaskField(to, from, field);
  if (to[field] === undefined) copyTaskField(to, NULL_TASK_FIELDS, field);
}

/** A ledger body (a task's update fields) as the wire's `PATCH /api/tasks/{id}` body. */
function taskUpdateBody(fields: TaskFields): Network.TaskUpdateAttributes {
  const body: Network.TaskUpdateAttributes = {};
  for (const field of Object.keys(fields)) {
    const known = TASK_UPDATE_FIELDS.find((f) => f === field);
    if (known != null) copyTaskField(body, fields, known);
  }
  return body;
}

/** Every update field `null` — what a field the view lacks restores to. */
const NULL_TASK_FIELDS: TaskFields = Object.freeze({
  title: null,
  description: null,
  dueDate: null,
  eventId: null,
  importance: null,
  position: null,
  scheduledStart: null,
  scheduledEnd: null,
  status: null,
  completedAt: null,
});

/**
 * A partial task view (an `Item`, a projected patch) as the wire's task
 * attributes: each attribute it does not carry reads as `null`.
 */
function taskAttributesOf(fields: Partial<Network.TaskAttributes>): Network.TaskAttributes {
  return Network.TaskAttributes.make({
    completedAt: null,
    createdAt: null,
    description: null,
    displayId: null,
    displayNumber: null,
    dueDate: null,
    eventId: null,
    importance: null,
    position: null,
    scheduledEnd: null,
    scheduledStart: null,
    status: null,
    title: null,
    ...fields,
  });
}
